/**
 * GlobalFiasAIContext — the ONE Fias AI assistant instance for the app.
 *
 * Mounted ABOVE <Routes> in App.tsx (never inside Layout — Layout is
 * instantiated per-route element, and moving this provider there would
 * silently lose the conversation, the WebSocket subscription, and any
 * in-flight agent turn on every navigation. Do not move it.)
 *
 * Responsibilities:
 * - Owns the agent chat engine (useFiasAIAgentChat) and the panel state.
 * - Holds the ARCHE REGISTRY: arche pages register their
 *   ArcheAICapabilities via useRegisterArcheAI on mount; the last
 *   registered (top of stack) is the "active" arche whose UI actions and
 *   state snapshot ride along with each turn. Capabilities are stored as
 *   refs and read at send/event time — never captured stale.
 * - Renders the GlobalFiasAIWindow as a sibling of children so the window
 *   floats over whatever page is mounted and survives navigation.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/hooks/useAuth';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { ArcheAICapabilities } from '../types';
import {
  HANDOFF_KIND_TARGET_ARCHE,
  handoffTargetsArche,
  parseArcheHandoffPayload,
  type ArcheHandoffPayload,
} from '../handoff';
import type { ArcheHandoffContext } from './useArcheHandoffIntake';
import { useFiasAIAgentChat, type UseFiasAIAgentChatReturn } from './useFiasAIAgentChat';

const MAR_ARCHE_ID = 'arche_make_a_request';
const SCHED_SEEN_KEY = 'fias-ai-scheduled-history-seen-at';
const SCHED_ALERTS_KEY = 'fias-ai-scheduled-alerts-enabled';

/** Preference default is ON; only an explicit "false" disables completed-task alerts. */
function readSchedAlertsEnabled(): boolean {
  try {
    return localStorage.getItem(SCHED_ALERTS_KEY) !== 'false';
  } catch {
    return true;
  }
}

/**
 * The user's last "viewed scheduled History" timestamp. Seeded to NOW on first
 * read so tasks that finished before the feature existed don't retroactively
 * alert; thereafter only completions after the last History view count.
 */
function readSchedSeenAt(): string {
  try {
    const existing = localStorage.getItem(SCHED_SEEN_KEY);
    if (existing) return existing;
    const now = new Date().toISOString();
    localStorage.setItem(SCHED_SEEN_KEY, now);
    return now;
  } catch {
    return new Date().toISOString();
  }
}

interface RegisteredArche {
  archeId: string;
  capsRef: MutableRefObject<ArcheAICapabilities>;
}

export interface GlobalFiasAIValue extends UseFiasAIAgentChatReturn {
  isPanelOpen: boolean;
  /** Collapsed to the floating button (chat state preserved, window hidden). */
  isMinimized: boolean;
  openPanel: () => void;
  closePanel: () => void;
  /** Collapse the window to a draggable button in the corner. */
  minimizePanel: () => void;
  togglePanel: () => void;
  /** The currently-open arche's id, or null on non-arche pages ("Platform" scope). */
  activeArcheId: string | null;
  /** Live capabilities of the active arche (read fresh; may be null). */
  getActiveCapabilities: () => ArcheAICapabilities | null;
  /** Registration for arche pages — use the useRegisterArcheAI hook, not this directly. */
  registerArche: (entry: RegisteredArche) => () => void;
  /**
   * Open a typed Arche Handoff (ref-card click, "open in X" affordances).
   * If the target arche is already open and its `acceptPrefill` handles the
   * payload, it applies in place; otherwise navigates to the arche with the
   * payload in navigation state (consumed on mount by useArcheHandoffIntake).
   * Arche-bound kinds derive their target; GENERIC kinds (open_document)
   * require `targetArcheId` — without one the call is a no-op.
   */
  /** Routes a typed handoff; `false` when the payload has no valid destination. */
  openHandoff: (
    payload: ArcheHandoffPayload,
    targetArcheId?: string,
    context?: ArcheHandoffContext,
  ) => boolean;
  /**
   * Count of FINISHED scheduled tasks (completed/failed) the user hasn't yet
   * viewed in Scheduled-tasks History. Drives the badge cascade: Fias AI header
   * button → schedules toggle → History tab. Always 0 when alerts are disabled.
   */
  scheduledAlertCount: number;
  /**
   * Mark scheduled History as seen — clears the whole cascade. Call on History
   * open. Returns the PRIOR "seen" watermark (before advancing it) so the caller
   * can flag the individual rows that finished since the last view.
   */
  markScheduledHistorySeen: () => string;
  /** Preference: raise an alert when a scheduled task finishes (default on). */
  scheduledAlertsEnabled: boolean;
  setScheduledAlertsEnabled: (enabled: boolean) => void;
}

const GlobalFiasAIContext = createContext<GlobalFiasAIValue | null>(null);

export function useGlobalFiasAI(): GlobalFiasAIValue {
  const ctx = useContext(GlobalFiasAIContext);
  if (!ctx) {
    throw new Error('useGlobalFiasAI must be used within GlobalFiasAIProvider');
  }
  return ctx;
}

/** Non-throwing variant for chrome that renders on unauthenticated pages. */
export function useGlobalFiasAIOptional(): GlobalFiasAIValue | null {
  return useContext(GlobalFiasAIContext);
}

export function GlobalFiasAIProvider({ children }: { children: React.ReactNode }) {
  // Tri-state: closed (nothing shown) / open (full window) / minimized
  // (collapsed to a draggable corner button; chat state is preserved).
  const [panelState, setPanelState] = useState<'closed' | 'open' | 'minimized'>('closed');
  const isPanelOpen = panelState === 'open';
  const isMinimized = panelState === 'minimized';
  // Stack of registered arches; last one wins (safe across route-transition
  // overlap where the next page mounts before the previous unmounts).
  const registryRef = useRef<RegisteredArche[]>([]);
  const [registryVersion, setRegistryVersion] = useState(0);

  const registerArche = useCallback((entry: RegisteredArche) => {
    registryRef.current = [...registryRef.current, entry];
    setRegistryVersion((v) => v + 1);
    return () => {
      registryRef.current = registryRef.current.filter((e) => e !== entry);
      setRegistryVersion((v) => v + 1);
    };
  }, []);

  const getActiveCapabilities = useCallback((): ArcheAICapabilities | null => {
    const top = registryRef.current[registryRef.current.length - 1];
    if (!top) return null;
    const caps = top.capsRef.current;
    return caps.enabled === false ? null : caps;
  }, []);

  const navigate = useNavigate();

  // Typed handoff open: in-place when the target arche is open and accepts,
  // navigation-with-state otherwise (same fiasAiPrefill channel open_arche uses).
  //
  // Returns whether the payload was ROUTED. `false` means it had nowhere valid
  // to go — a generic kind with no explicit target, or a target that would
  // refuse it — so the caller can decide what to do rather than having the
  // handoff evaporate inside this function.
  const openHandoff = useCallback(
    (
      payload: ArcheHandoffPayload,
      targetArcheId?: string,
      context?: ArcheHandoffContext,
    ): boolean => {
      const target = targetArcheId ?? HANDOFF_KIND_TARGET_ARCHE[payload.kind];
      // A generic kind with no explicit target (or a target that would refuse
      // the payload) has nowhere valid to go — drop rather than misnavigate.
      if (!target || !handoffTargetsArche(payload, target)) return false;
      // The in-place path (`acceptPrefill`) carries the PAYLOAD only — it has
      // no channel for host context. When there is context to deliver, take
      // the navigation path so it arrives; applying in place would silently
      // drop the origin, and a handoff whose origin is gone cannot be
      // returned to. Slightly less slick, never wrong.
      if (!context) {
        const top = registryRef.current[registryRef.current.length - 1];
        if (top?.archeId === target) {
          const caps = top.capsRef.current;
          if (caps.enabled !== false && caps.acceptPrefill?.(payload)) return true;
        }
      }
      navigate(`/a/${target}`, {
        state: {
          fiasAiPrefill: payload,
          // Beside the payload, never inside it, so a producer cannot name
          // its own origin. Host-derived; never attested.
          ...(context?.originArcheId
            ? { fiasAiHandoffContext: { originArcheId: context.originArcheId } }
            : {}),
        },
      });
      return true;
    },
    [navigate],
  );

  // The assistant's open_arche. With a prefill it is a handoff and takes the
  // same path as a ref-card click — applied IN PLACE when the target arche is
  // already open and accepts it, so "open the composer" from inside Email
  // never remounts the mailbox. (The server already answers a prefill-less
  // open_arche on the open arche with `alreadyOpen` and never reaches here.)
  const handleNavigate = useCallback(
    (archeId: string, prefill?: Record<string, unknown>) => {
      if (prefill) {
        const payload = parseArcheHandoffPayload(prefill);
        // Only RETURN when the handoff actually routed. A payload this arche
        // would refuse must not evaporate here: the server has already told
        // the model `ok` and the tool card already reads "Opened <arche>", so
        // dropping it silently is the very "claimed it did something it did
        // not" failure this path exists to prevent. Fall through to a plain
        // navigate — the user still moves and the intake ignores a prefill it
        // cannot use, which is the honest degradation.
        //
        // open_arche's own superRefine validates targeting against the
        // CANONICAL map, so this only diverges if the client mirror drifts
        // from it (pinned by tests/architecture/arche-handoff-registry.test.ts).
        if (payload && openHandoff(payload, archeId)) return;
      }
      navigate(`/a/${archeId}`, prefill ? { state: { fiasAiPrefill: prefill } } : undefined);
    },
    [navigate, openHandoff],
  );

  const chat = useFiasAIAgentChat({
    getCapabilities: getActiveCapabilities,
    onNavigate: handleNavigate,
  });

  // --- Scheduled-task completion alerts (Fias-AI-scoped, NOT the Worktop) ---
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [scheduledAlertsEnabled, setScheduledAlertsEnabledState] =
    useState<boolean>(readSchedAlertsEnabled);

  const scheduledAlertQuery = useQuery({
    queryKey: queryKeys.fiasAI.scheduledAlertCount(),
    // seenAt is read fresh inside the queryFn (not in the key) so markSeen +
    // invalidate is enough to re-derive the count with the new watermark, and
    // every consumer (header, toggle, tab) shares this one query.
    queryFn: () =>
      api.getCompletedScheduleCount({ archeId: MAR_ARCHE_ID, since: readSchedSeenAt() }),
    enabled: isAuthenticated && scheduledAlertsEnabled,
    refetchInterval: 30_000,
  });
  const scheduledAlertCount = scheduledAlertsEnabled ? (scheduledAlertQuery.data?.count ?? 0) : 0;

  // Returns the watermark that was active for THIS view (i.e. the previous
  // "seen" time) BEFORE advancing it to now — so the caller can highlight the
  // individual history rows that finished since the user last looked. The
  // whole-badge cascade still clears via the invalidate below.
  const markScheduledHistorySeen = useCallback((): string => {
    let prior: string;
    try {
      prior = readSchedSeenAt();
      localStorage.setItem(SCHED_SEEN_KEY, new Date().toISOString());
    } catch {
      prior = new Date().toISOString();
    }
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.scheduledAlertCount() });
    return prior;
  }, [queryClient]);

  const setScheduledAlertsEnabled = useCallback(
    (enabled: boolean) => {
      try {
        localStorage.setItem(SCHED_ALERTS_KEY, String(enabled));
      } catch {
        /* non-fatal */
      }
      setScheduledAlertsEnabledState(enabled);
      if (enabled) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.scheduledAlertCount() });
      }
    },
    [queryClient],
  );

  const openPanel = useCallback(() => setPanelState('open'), []);
  const closePanel = useCallback(() => setPanelState('closed'), []);
  const minimizePanel = useCallback(() => setPanelState('minimized'), []);
  // Header button: open ⇄ closed, and restore from minimized to open.
  const togglePanel = useCallback(
    () => setPanelState((s) => (s === 'open' ? 'closed' : 'open')),
    [],
  );

  const activeArcheId = registryRef.current[registryRef.current.length - 1]?.archeId ?? null;

  const value = useMemo<GlobalFiasAIValue>(
    () => ({
      ...chat,
      isPanelOpen,
      isMinimized,
      openPanel,
      closePanel,
      minimizePanel,
      togglePanel,
      activeArcheId,
      getActiveCapabilities,
      registerArche,
      openHandoff,
      scheduledAlertCount,
      markScheduledHistorySeen,
      scheduledAlertsEnabled,
      setScheduledAlertsEnabled,
    }),
    // registryVersion is a deliberate dependency: registrations must
    // re-render consumers (scope chip) even though the data lives in a ref.
    [
      chat,
      panelState,
      openPanel,
      closePanel,
      minimizePanel,
      togglePanel,
      activeArcheId,
      getActiveCapabilities,
      registerArche,
      openHandoff,
      registryVersion,
      scheduledAlertCount,
      markScheduledHistorySeen,
      scheduledAlertsEnabled,
      setScheduledAlertsEnabled,
    ],
  );

  // ALWAYS provide the context — including on unauthenticated pages.
  // An earlier auth bail-out here returned {children} without the
  // Provider, which crashed GlobalFiasAIWindow (a child) on the logged-
  // out front page and during auth hydration. The bail-out sat below
  // every hook, so it saved no work anyway; who can OPEN the panel is
  // gated where it belongs — the header button renders only for
  // authenticated users, and the window renders nothing while closed.
  return <GlobalFiasAIContext.Provider value={value}>{children}</GlobalFiasAIContext.Provider>;
}
